'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import CheckmarkWrapper from '@/components/common/CheckmarkWrapper'

export type DirectionType = 'ltr' | 'rtl'

const directions: DirectionType[] = ['ltr', 'rtl']
const defaultDirection: DirectionType = 'ltr'

type DirectionContextType = {
  direction: DirectionType
  setDirection: (direction: DirectionType) => void
}

const DirectionContext = createContext<DirectionContextType>({
  direction: defaultDirection,
  setDirection: () => null,
})

export const useDirectionContext = () => {
  return useContext(DirectionContext)
}

// Utility function
const getDirection = () => {
  // client
  // prettier-ignore
  if (typeof window !== 'undefined') { // NOSONAR
    // NOTE: Any item from localStorage could be compromised by the user (e.g. edited, deleted).
    // @@TODO: Validate input from localStorage.
    const from_localStorage = window.localStorage.getItem('dash.direction') // NOSONAR
    console.log('from_localStorage(dash.direction)=', from_localStorage)
    if (from_localStorage) {
      return from_localStorage as DirectionType
    } else {
      window.localStorage.setItem('dash.direction', defaultDirection) // NOSONAR
      return defaultDirection
    }
  }
  // server
  return defaultDirection
}

export const DirectionProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [direction, setDirection] = useState<DirectionType>(getDirection())

  return (
    <DirectionContext.Provider value={{ direction, setDirection }}>
      {children}
    </DirectionContext.Provider>
  )
}

const DirectionSelect = () => {
  const [mounted, setMounted] = useState(false)
  const { direction, setDirection } = useDirectionContext()

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  const toggleDirection = () => {
    const newDirection = direction === 'ltr' ? 'rtl' : 'ltr'
    window.localStorage.setItem('dash.direction', newDirection) // NOSONAR
    setDirection(newDirection)
  }

  const keyHandler = useCallback((event: KeyboardEvent) => {
    console.log('toggleControlKey() - a key pressed')
    if (event.isComposing || event.key === 'Control') {
      console.log('toggleControlKey() - control key pressed')
      // toggleDirection()
    }
  }, [])

  // Ref for the event handler function to maintain identity between renders
  const handlerRef = useRef<((event: KeyboardEvent) => void) | null>(null)

  useEffect(
    function update() {
      window.localStorage.setItem('dash.direction', direction) // NOSONAR
    },
    [direction],
  )

  useEffect(
    function setKeyHandler() {
      handlerRef.current = keyHandler
    },
    [keyHandler],
  )

  useEffect(function setupListeners() {
    // Prevent adding unlimited event listeners (due to hot reload in dev mode).
    const listener = (event: KeyboardEvent) => handlerRef.current?.(event)
    window.addEventListener('keydown', listener) // NOSONAR
    return function cleanupListeners() {
      window.removeEventListener('keydown', listener) // NOSONAR
    }
  }, [])

  return (
    <div>
      <Listbox value={direction} onChange={setDirection}>
        <ListboxButton className="focus-custom flex w-16 h-7 rounded-md justify-center items-center border-custom">
          {mounted ? (
            <div className="flex">
              <div className="truncate">{direction}</div>
              <div className="px-1"></div>
              <ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
            </div>
          ) : (
            <div></div>
          )}
        </ListboxButton>
        <ListboxOptions
          /* @@TODO: Check if overflow-hidden is needed (ALL BROWSERS!) and apply to all other Menu / ListBox */
          className="absolute Qmt-10 w-fill rounded-md shadow-md shadow-violet-600 dark:shadow-violet-600 bg-background-light text-foreground-light dark:bg-background-dark dark:text-foreground-dark
          data-enter:transition data-enter:ease-out data-enter:duration-100
          data-enter-[from]:transform data-enter-[from]:opacity-0 data-enter-[from]:scale-95
          data-enter-[to]:transform data-enter-[to]:opacity-1000 data-enter-[to]:scale-100
          data-leave:transition data-leave:ease-in data-leave:duration-75
          data-leave-[from]:transform data-leave-[from]:opacity-100 data-leave-[from]:scale-100
          data-leave-[to]:transform data-leave-[to]:opacity-0 data-leave-[to]:scale-95"
          //anchor="top"
          modal={false}
          transition
        >
          {directions.map((direction) => (
            <div className="px-1 first:mt-1 last:mb-1" key={direction}>
              <ListboxOption
                key={direction}
                className="cursor-default select-none pe-8 group flex w-full items-center rounded-md px-2 py-2 data-focus:bg-gray-200 dark:data-focus:bg-gray-700"
                value={direction}
              >
                <CheckmarkWrapper text={direction} />
              </ListboxOption>
            </div>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  )
}

export default DirectionSelect
